import React, { useEffect, useMemo, useRef, useState } from "react";
import HomeScreenView from "./HomeScreenView";
import SetupWizard from "./SetupWizard";
import SearchSheet from "./SearchSheet";
import SettingsPanel from "./SettingsPanel";
import MonthTimetable from "./MonthTimetable";
import LiveNow from "./LiveNow";
import LiveVideo from "./LiveVideo";
import PosterViewer from "./PosterViewer";
import BrandMark from "./BrandMark";
import CachedImage from "../shared/CachedImage";
import { useLiveAudio } from "./useLiveAudio";
import { useAzanAlerts } from "./useAzanAlerts";
import { useWakeSchedule } from "./useWakeSchedule";
import { clearOwnAzan } from "./ownAzan";
import { useBackCloses } from "./useBackCloses";
import { useRemoteFocus } from "./useRemoteFocus";
import { useTvTextBoxes } from "./useTvTextBoxes";
import { useOfflineCopies } from "./useOfflineCopies";
import { usePosters } from "./homePosters";
import { useHomeLicence } from "./homeLicence";
import { canTurnTv, isNativeApp, turnTv } from "./nativeApp";
import { loadMasjidIndex } from "./masjidIndex";
import { HouseholdWordingContext } from "./householdWording";
import { fetchScreenData } from "../../services/api";
import { screenDB } from "../../db/screenDb";
import { runtimeConfig } from "../../config/runtime";
import { useConnectionQuality } from "../../hooks/useConnectionQuality";
import { ScreenData } from "../../types/ScreenTypes";
import {
  HomeMasjid,
  HomeSettings,
  MAX_FAVOURITES,
  loadHomeSettings,
  previewSettings,
  saveHomeSettings,
} from "./homeSettings";
import "./HomeShell.css";

// In the app, Android hands any outside link to the YouTube app and Home stays underneath.
const openOutside = (url: string) => {
  if (isNativeApp()) window.location.href = url;
  else window.open(url, "_blank", "noopener");
};

const IDLE_RETURN_MS = 5 * 60 * 1000;
const SWIPE_MIN_PX = 80;
// How long the bar stays after a touch or a key, over a masjid's own screen.
const BAR_SHOWS_MS = 10_000;

// "masjids": the list of favourites, opened from the name in the bar. "watch": Watch tapped while
// the masjid isn't live on YouTube. "posters": the masjid's posters, opened from the bar.
type Sheet = "search" | "settings" | "month" | "masjids" | "watch" | "posters" | null;

const useIsPortrait = () => {
  const [portrait, setPortrait] = useState(() => window.innerHeight > window.innerWidth);
  useEffect(() => {
    const update = () => setPortrait(window.innerHeight > window.innerWidth);
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  return portrait;
};

const useWakeLock = () => {
  useEffect(() => {
    // The app holds the screen on itself, and lets it go after waking it for an azan.
    if (isNativeApp()) return;
    let lock: WakeLockSentinel | null = null;
    const request = () =>
      navigator.wakeLock
        ?.request("screen")
        .then((sentinel) => (lock = sentinel))
        .catch(() => {}); // refused, e.g. battery saver; the Android app keeps the screen on anyway
    const onVisible = () => document.visibilityState === "visible" && request();
    request();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      lock?.release().catch(() => {});
    };
  }, []);
};

// The page never closes on a home tablet, so a deployed update would wait forever
// ("prompt" service worker): apply it overnight instead. Each tablet picks its own minute between
// 2am and 5am, so a deploy's reloads (and the screen data each one fetches) don't all land at once.
const UPDATE_MINUTE = 120 + Math.floor(Math.random() * 180);

const useNightlyUpdate = () => {
  useEffect(() => {
    let timer = 0;
    const schedule = () => {
      const next = new Date();
      next.setHours(0, UPDATE_MINUTE, 0, 0);
      if (next.getTime() <= Date.now()) next.setDate(next.getDate() + 1);
      timer = window.setTimeout(apply, next.getTime() - Date.now());
    };
    const apply = async () => {
      try {
        const registration = await navigator.serviceWorker?.getRegistration();
        await registration?.update();
        const installing = registration?.installing;
        if (installing) {
          await new Promise((resolve) =>
            installing.addEventListener("statechange", () => {
              if (installing.state !== "installing") resolve(null);
            }),
          );
        }
        if (registration?.waiting) {
          navigator.serviceWorker.addEventListener("controllerchange", () => window.location.reload(), {
            once: true,
          });
          registration.waiting.postMessage({ type: "SKIP_WAITING" });
          return;
        }
      } catch {
        // Offline: try again tomorrow.
      }
      schedule();
    };
    schedule();
    return () => window.clearTimeout(timer);
  }, []);
};

// The household tablet: the favourite masjids' screens with a bar to switch, search and settings.
const HomeShell: React.FC = () => {
  const [settings, setSettings] = useState<HomeSettings | null>(loadHomeSettings);
  const [current, setCurrent] = useState(0);
  const [visiting, setVisiting] = useState<HomeMasjid | null>(null);
  const [sheet, setSheet] = useState<Sheet>(null);
  // "failed": never saved on this tablet and no internet to fetch it.
  const [monthScreen, setMonthScreen] = useState<ScreenData | "failed" | null>(null);
  const portrait = useIsPortrait();
  // YouTube channel links by masjid id, from the masjid index, for the Watch button.
  const [youtube, setYoutube] = useState<Record<number, string>>({});
  const idleTimer = useRef(0);
  const [barAwake, setBarAwake] = useState(false);
  const barTimer = useRef(0);
  const keyHandler = useRef<(e: KeyboardEvent) => void>(() => {});
  const swipeStart = useRef<{ x: number; y: number; t: number } | null>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const dropFocus = useRemoteFocus(shellRef);
  useTvTextBoxes();
  const [endedNoticeClosed, setEndedNoticeClosed] = useState(false);

  const licence = useHomeLicence();
  // What runs: the household's settings with a code, otherwise the preview.
  const active = useMemo(
    () => settings && (licence.licensed ? settings : previewSettings(settings)),
    [settings, licence.licensed],
  );
  const offline = useConnectionQuality() === "offline";
  const live = useLiveAudio(active, offline);
  // A partner's player that started itself is the masjid's broadcast too: it carries the azan.
  const azan = useAzanAlerts(
    active,
    !!live.playing || !!live.partnerPlayer?.startsItself,
    live.makeWayForAzan,
  );
  useWakeLock();
  useWakeSchedule(
    licence.licensed ? active : null,
    !!live.playing || !!live.partnerPlayer || !!live.video || azan.playing,
  );
  useOfflineCopies(licence.licensed ? settings : null);
  // The app also remembers it, so a TV on its side starts the right way up.
  const tvPosition = settings?.tvPosition;
  useEffect(() => {
    if (settings && canTurnTv()) turnTv(tvPosition);
  }, [tvPosition]); // eslint-disable-line react-hooks/exhaustive-deps
  useNightlyUpdate();
  // Posters come with a code, for the masjid on screen.
  const onScreen =
    active && (visiting ?? active.favourites[Math.min(current, active.favourites.length - 1)]);
  const { posters, colours, seen, markSeen } = usePosters(
    onScreen?.id ?? 0,
    portrait,
    licence.licensed && !!onScreen,
  );
  useBackCloses(!!sheet || !!visiting || !!live.video || !!live.partnerPlayer, () => {
    setSheet(null);
    setVisiting(null);
    live.closeVideo();
    live.closePartnerPlayer();
  });

  useEffect(
    () => () => {
      window.clearTimeout(idleTimer.current);
      window.clearTimeout(barTimer.current);
    },
    [],
  );

  // On the document, not the shell: when a TV opens Home nothing in it has focus yet, so a remote's
  // key goes to the page itself.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (shellRef.current) keyHandler.current(e);
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, []);

  useEffect(() => {
    loadMasjidIndex()
      .then((all) =>
        setYoutube(Object.fromEntries(all.filter((m) => m.youtube).map((m) => [m.id, m.youtube!]))),
      )
      .catch(() => {});
  }, []);

  const update = (next: HomeSettings | null) => {
    saveHomeSettings(next);
    setSettings(next);
  };

  const goHome = () => {
    setVisiting(null);
    setCurrent(0);
    setSheet(null);
  };

  if (!settings || !active) {
    return (
      <SetupWizard
        licence={licence}
        onDone={(chosen) => {
          update(chosen);
          goHome();
        }}
      />
    );
  }

  const { favourites } = active;
  const index = Math.min(current, favourites.length - 1);
  const shown = visiting ?? favourites[index];
  const styleSuffix =
    active.screenStyle === "notices" ? "" : active.screenStyle === "next" ? "-next" : "-simple";
  const layoutCode = `${portrait ? "home-portrait" : "home"}${styleSuffix}`;
  // The favourite's own masjid screen, if the household picked one; otherwise Home's layout.
  const masjidScreen = visiting ? undefined : active.masjidScreens?.[shown.id];
  const screenCode = masjidScreen ? `home-s${masjidScreen}` : layoutCode;
  // A masjid's screen has no strip for the bar, so the bar waits out of sight until a touch or a
  // key, or until something needs it.
  // An open sheet or layer keeps it too, so no key meant for Settings is spent on the bar.
  const barNeeded =
    !!sheet ||
    !!live.prompt ||
    !!live.notLive ||
    !!live.playing ||
    !!live.partnerPlayer ||
    !!live.video ||
    azan.playing ||
    azan.blocked;
  const barHidden = !!masjidScreen && !barNeeded && !barAwake;

  const wakeBar = () => {
    setBarAwake(true);
    window.clearTimeout(barTimer.current);
    barTimer.current = window.setTimeout(() => setBarAwake(false), BAR_SHOWS_MS);
  };

  // A touch or a TV remote's key.
  const restartIdle = () => {
    window.clearTimeout(idleTimer.current);
    idleTimer.current = window.setTimeout(() => {
      dropFocus();
      goHome();
    }, IDLE_RETURN_MS);
  };

  const onActivity = (e: React.PointerEvent) => {
    restartIdle();
    wakeBar();
    swipeStart.current = { x: e.clientX, y: e.clientY, t: Date.now() };
  };

  keyHandler.current = (e: KeyboardEvent) => {
    restartIdle();
    // The first key only brings the bar back, so OK can't press a button the household can't see.
    if (barHidden) {
      e.preventDefault();
      e.stopPropagation();
    }
    wakeBar();
  };

  const onSwipeEnd = (e: React.PointerEvent) => {
    const start = swipeStart.current;
    swipeStart.current = null;
    if (!start || sheet || visiting || favourites.length < 2) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dy) > Math.abs(dx) || Date.now() - start.t > 800) {
      return;
    }
    setCurrent((index + (dx < 0 ? 1 : favourites.length - 1)) % favourites.length);
  };

  const view = (masjid: HomeMasjid) => {
    setSheet(null);
    const favourite = favourites.findIndex((m) => m.id === masjid.id);
    setVisiting(favourite >= 0 ? null : masjid);
    if (favourite >= 0) setCurrent(favourite);
  };

  const addFavourite = (masjid: HomeMasjid) =>
    update({ ...settings, favourites: [...settings.favourites, masjid] });

  // The display behind has already saved this masjid's screen on the tablet, so the month opens
  // from that copy at once instead of flashing "Loading…"; the fetch then brings it up to date.
  const openMonth = async () => {
    setMonthScreen(await screenDB.getScreenData(`${shown.id}:${layoutCode}`));
    setSheet("month");
    fetchScreenData(shown.id, layoutCode)
      .then(setMonthScreen)
      .catch(() => setMonthScreen((saved) => saved ?? "failed"));
  };

  return (
    <div
      ref={shellRef}
      className={`hs-shell${portrait ? " portrait" : ""}${active.look === "mono" ? " hs-mono" : ""}${
        masjidScreen ? " hs-masjid-screen" : ""
      }`}
      onPointerDownCapture={onActivity}
      onPointerUpCapture={onSwipeEnd}
    >
      <HouseholdWordingContext.Provider value={settings.wording ?? null}>
        <HomeScreenView
          key={`${shown.id}:${screenCode}`}
          masjidId={shown.id}
          screenCode={screenCode}
        />
      </HouseholdWordingContext.Provider>

      <nav className={`hs-bar${barHidden ? " hs-bar-hidden" : ""}`}>
        {live.prompt ? (
          <div className="hs-bar-message" role="alert">
            <span className="hs-live-dot" />
            <span className="hs-bar-text">
              <strong>{live.prompt.name}</strong>
              <span className="hs-wide-only"> is live now</span>
            </span>
            <button className="hs-bar-button hs-primary" onClick={() => live.listen(live.prompt!)}>
              Listen
            </button>
            <button className="hs-bar-button" onClick={live.dismissPrompt} aria-label="Not now">
              <span className="hs-wide-only">Not now</span>
              <span className="hs-narrow-only">✕</span>
            </button>
          </div>
        ) : live.notLive ? (
          <div className="hs-bar-message" role="status">
            <span className="hs-bar-text">{live.notLive.name} isn't live right now.</span>
          </div>
        ) : visiting ? (
          <>
            <button className="hs-bar-button" onClick={goHome}>
              ← Back to home
            </button>
            <span className="hs-bar-title">{visiting.name}</span>
            {licence.licensed && favourites.length < MAX_FAVOURITES && (
              <button
                className="hs-bar-button"
                onClick={() => {
                  addFavourite(visiting);
                  setVisiting(null);
                  setCurrent(favourites.length);
                }}
              >
                ☆ Add to favourites
              </button>
            )}
          </>
        ) : (
          // Only the masjid on screen, so its name is never cut; the others are in the list.
          <button
            className="hs-bar-button hs-current"
            onClick={() => favourites.length > 1 && setSheet("masjids")}
            aria-label={favourites.length > 1 ? `${shown.name}: switch masjid` : undefined}
          >
            {index === 0 && <span aria-label="Home">★</span>}
            <span className="hs-current-name">{shown.name}</span>
            {favourites.some((m) => m.id !== shown.id && live.liveIds.has(m.id)) && (
              <span className="hs-live-dot" aria-label="Another favourite is live" />
            )}
            {favourites.length > 1 && <span className="hs-caret">▾</span>}
          </button>
        )}
        <span className="hs-bar-spacer" />
        {/* Always there for a masjid with live audio, so a household can check for itself. */}
        {shown.audio &&
          !offline &&
          live.playing?.id !== shown.id &&
          live.partnerPlayer?.masjid.id !== shown.id &&
          live.prompt?.id !== shown.id && (
            <button
              className={`hs-bar-button${live.liveIds.has(shown.id) ? " hs-primary" : ""}`}
              onClick={() => live.listen(shown)}
              aria-label={`Listen to ${shown.name}`}
            >
              {live.liveIds.has(shown.id) && <span className="hs-live-dot" />}
              🔊<span className="hs-wide-only"> Listen</span>
            </button>
          )}
        {/* Always there for a masjid with YouTube: live plays here, otherwise it asks first. */}
        {(live.liveVideo[shown.id] || youtube[shown.id]) && !offline && !live.video && (
          <button
            className={`hs-bar-button${live.liveVideo[shown.id] ? " hs-live" : ""}`}
            onClick={() =>
              live.liveVideo[shown.id]
                ? live.watch(shown, live.liveVideo[shown.id])
                : setSheet("watch")
            }
          >
            ▶<span className="hs-wide-only"> {live.liveVideo[shown.id] ? "Watch live" : "Watch"}</span>
          </button>
        )}
        {azan.blocked && (
          <button className="hs-bar-button hs-live" onClick={azan.playBlocked}>
            🔊 Azan<span className="hs-wide-only"> · Tap to play</span>
          </button>
        )}
        {azan.playing && (
          <button className="hs-bar-button hs-live" onClick={azan.stop} aria-label="Stop the azan">
            🔊 Azan · Stop
          </button>
        )}
        {live.playing && (
          <button className="hs-bar-button hs-live" onClick={live.stop} aria-label="Stop listening">
            <span className="hs-live-dot" /> LIVE
            <span className="hs-wide-only"> · {live.playing.name}</span> · Stop
          </button>
        )}
        {!licence.licensed && (
          <button className="hs-bar-button hs-preview" onClick={() => setSheet("settings")}>
            {licence.licence?.ended ? "Code needed" : "Preview"}
          </button>
        )}
        {posters.length > 0 && (
          <button
            className="hs-bar-button"
            onClick={() => setSheet(sheet === "posters" ? null : "posters")}
          >
            {posters.some((poster) => !seen.includes(poster.id)) && (
              <span className="hs-new-dot" aria-label="New poster" />
            )}
            Posters
          </button>
        )}
        <button className="hs-bar-button" onClick={openMonth}>
          Month
        </button>
        <button className="hs-bar-button" onClick={() => setSheet("search")}>
          Search
        </button>
        {/* Right end: the player's offline icon is pinned to the bottom-left corner. Always on
            screen, so it's the way into Settings. */}
        <button className="hs-brand-button" onClick={() => setSheet("settings")} aria-label="Settings">
          <BrandMark className="hs-brand" compact={portrait} />
        </button>
      </nav>

      {sheet === "posters" && posters.length > 0 && (
        <PosterViewer
          posters={posters}
          colours={colours}
          seen={seen}
          onSeen={markSeen}
          onClose={() => setSheet(null)}
        />
      )}

      {sheet === "watch" && youtube[shown.id] && (
        <div className="hs-partner hs-watch" role="dialog" aria-label="Watch">
          <div className="hs-partner-head">
            <span>{shown.name} isn't live on YouTube right now.</span>
            <button className="hs-close" onClick={() => setSheet(null)} aria-label="Close">
              ✕
            </button>
          </div>
          <p className="hs-watch-note">A broadcast that has just started can take a few minutes to show here.</p>
          <div className="hs-watch-actions">
            <button
              className="hs-button hs-primary"
              onClick={() => {
                setSheet(null);
                openOutside(youtube[shown.id]);
              }}
            >
              Open their YouTube channel
            </button>
            <button className="hs-button" onClick={() => setSheet(null)}>
              Close
            </button>
          </div>
        </div>
      )}

      {licence.licence?.ended && !endedNoticeClosed && !sheet && (
        <div className="hs-partner hs-watch" role="alert">
          <div className="hs-partner-head">
            <span>This tablet's code no longer works.</span>
            <button className="hs-close" onClick={() => setEndedNoticeClosed(true)} aria-label="Close">
              ✕
            </button>
          </div>
          <p className="hs-watch-note">
            The subscription may have ended, or the tablet was removed from the code. The prayer
            times carry on.
          </p>
          <div className="hs-watch-actions">
            <button className="hs-button hs-primary" onClick={() => setSheet("settings")}>
              Enter a code
            </button>
            <button className="hs-button" onClick={() => setEndedNoticeClosed(true)}>
              Close
            </button>
          </div>
        </div>
      )}

      {live.partnerPlayer && (
        <div className="hs-partner">
          <div className="hs-partner-head">
            <span className="hs-partner-title">
              <span className="hs-live-dot" />
              <span className="hs-partner-name">{live.partnerPlayer.masjid.name}</span>
              {/* Their player starts only from its own button, unless the app presses it. */}
              {!live.partnerPlayer.startsItself && (
                <span className="hs-partner-hint">· tap ▶ to listen</span>
              )}
            </span>
            <button
              className="hs-close"
              onClick={live.closePartnerPlayer}
              aria-label="Close player"
            >
              ✕
            </button>
          </div>
          <iframe
            src={live.partnerPlayer.url}
            title={`${live.partnerPlayer.masjid.name} live audio`}
            allow="autoplay"
          />
        </div>
      )}

      {live.video && (
        <LiveVideo
          masjid={live.video.masjid}
          videoId={live.video.videoId}
          onClose={live.closeVideo}
        />
      )}

      {sheet === "search" && (
        <SearchSheet
          favourites={favourites}
          onView={view}
          onAdd={licence.licensed ? addFavourite : undefined}
          onClose={() => setSheet(null)}
          liveNow={
            <LiveNow
              favourites={favourites}
              liveIds={live.liveIds}
              liveVideo={live.liveVideo}
              livePartners={live.livePartners}
              findLiveNow={live.findLiveNow}
              onListen={(masjid) => {
                setSheet(null);
                live.listen(masjid);
              }}
              onWatch={(masjid, videoId) => {
                setSheet(null);
                live.watch(masjid, videoId);
              }}
            />
          }
          renderListen={(masjid) =>
            masjid.audio && (
              <button
                className="hs-button"
                onClick={() => {
                  setSheet(null);
                  live.listen(masjid);
                }}
              >
                Listen
              </button>
            )
          }
        />
      )}

      {sheet === "settings" && (
        <SettingsPanel
          settings={settings}
          licence={licence}
          onChange={update}
          onReset={() => {
            update(null);
            // A tablet handed on mustn't keep the last household's recordings.
            clearOwnAzan();
            goHome();
          }}
          onClose={() => setSheet(null)}
        />
      )}

      {sheet === "masjids" && (
        <>
          {/* Out of a remote's reach: Back closes the list there. */}
          <button
            className="hs-switch-scrim"
            onClick={() => setSheet(null)}
            aria-label="Close"
            tabIndex={-1}
          />
          <div className="hs-switch" role="dialog" aria-modal="true" aria-label="Your masjids">
            <h2>Your masjids</h2>
            {favourites.map((masjid, i) => (
              <button
                key={masjid.id}
                className="hs-switch-row"
                onClick={() => {
                  setCurrent(i);
                  setSheet(null);
                }}
              >
                <span className="hs-switch-photo">
                  <CachedImage
                    src={`${runtimeConfig.storageUrl}/masaajidimages/${masjid.id}.png`}
                    alt=""
                    className="hs-switch-photo-img"
                  />
                </span>
                <span className="hs-switch-text">
                  <strong>
                    {i === 0 && "★ "}
                    {masjid.name}
                  </strong>
                  <span className="hs-row-town">
                    {[masjid.town, i === 0 && "Home"].filter(Boolean).join(" · ")}
                  </span>
                </span>
                {(live.liveIds.has(masjid.id) || live.liveVideo[masjid.id]) && (
                  <span className="hs-switch-live">
                    <span className="hs-live-dot" />
                    {live.liveIds.has(masjid.id) ? "Live now" : "Live on YouTube"}
                  </span>
                )}
                {i === index && (
                  <span className="hs-switch-tick" aria-label="On screen">
                    ✓
                  </span>
                )}
              </button>
            ))}
          </div>
        </>
      )}

      {sheet === "month" &&
        (monthScreen && monthScreen !== "failed" ? (
          // MonthTimetable is also a page of its own, so the layer is marked here.
          <div className="hs-layer" role="dialog" aria-modal="true" aria-label="Month">
            <MonthTimetable
              masjidId={shown.id}
              masjidName={monthScreen.masjidName || shown.name}
              masjidColor={monthScreen.masjidColor}
              timeZone={monthScreen.timeZone}
              use12HourFormat={monthScreen.use12HourFormat}
              prayerNames={{ ...monthScreen.customizedNames, ...settings.wording?.names }}
              jamaahWord={settings.wording?.jamaat || undefined}
              onClose={() => setSheet(null)}
            />
          </div>
        ) : (
          <div className="hs-sheet hs-loading" role="dialog" aria-modal="true" aria-label="Month">
            <p>
              {monthScreen === "failed"
                ? `${shown.name}'s timetable needs the internet once before it works offline.`
                : `Loading ${shown.name}'s timetable…`}
            </p>
            <button className="hs-button" onClick={() => setSheet(null)}>
              Close
            </button>
          </div>
        ))}
    </div>
  );
};

export default HomeShell;
